/**
 * Forgiving search, ranked — for the search boxes over a list on screen.
 *
 * Case, punctuation and "&" vs "and" are ignored, words may come in any order,
 * and each word of the query only has to START a word of the text ("cons gold"
 * finds "Conservative Moderate Gold & Equity"). An id is matched exactly or by
 * its start. Results are ranked: an exact id, then an id that starts with the
 * query, then text that starts with it, text that contains it, and last text
 * whose words merely all appear.
 */

/** Text as compared: lower case, "&" as "and", punctuation as spaces. */
export const searchText = (s) => String(s ?? '')
  .toLowerCase()
  .replace(/&/g, ' and ')
  /* Letters (with their combining marks, so Devanagari stays whole) and digits. */
  .replace(/[^\p{L}\p{M}\p{N}]+/gu, ' ')
  .trim();

/**
 * How well `query` matches a record: 0 for no match, higher for better.
 * `ids` are compared whole or by their start; `texts` word by word.
 */
export function searchScore(query, { ids = [], texts = [] }) {
  const q = searchText(query);
  if (!q) return 1;
  const idList = ids.filter((v) => v != null && v !== '').map((v) => searchText(v));
  if (idList.some((id) => id === q)) return 100;
  if (idList.some((id) => id.startsWith(q))) return 60;

  const textList = texts.filter((v) => v != null && v !== '').map((v) => searchText(v));
  const words = [...idList, ...textList].join(' ').split(' ').filter(Boolean);
  const matched = q.split(' ').every((t) => words.some((w) => w.startsWith(t)));
  if (!matched) return 0;
  if (textList.some((t) => t.startsWith(q))) return 50;
  if (textList.some((t) => t.includes(q))) return 25;
  return 10;
}

/* ── similarity search ────────────────────────────────────────────────── */

/** The letter trigrams of a word, padded so its start and end count. */
function trigrams(s) {
  const t = `  ${s} `;
  const out = new Set();
  for (let i = 0; i < t.length - 2; i += 1) out.add(t.slice(i, i + 3));
  return out;
}

/** Dice similarity of two strings' trigrams, 0..1. */
export function trigramSimilarity(a, b) {
  if (!a || !b) return 0;
  const A = trigrams(a);
  const B = trigrams(b);
  let common = 0;
  A.forEach((g) => { if (B.has(g)) common += 1; });
  return (2 * common) / (A.size + B.size);
}

/** Edit distance, stopping early once it passes `max`. */
function editDistance(a, b, max = 2) {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i += 1) {
    const cur = [i];
    let best = i;
    for (let j = 1; j <= b.length; j += 1) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
      best = Math.min(best, cur[j]);
    }
    if (best > max) return max + 1;
    prev = cur;
  }
  return prev[b.length];
}

/**
 * How SIMILAR a security is to what was typed — for search-as-you-type over
 * names and symbols, where the reader types a fragment ("radi" → Radico
 * Khaitan), a few words in any order ("hdfc bank"), a bare ticker ("INFY" →
 * INFY.NSE) or a misspelling ("relaince"). 0 means unrelated; higher is closer.
 *
 *   100  symbol exactly (with or without its exchange suffix)
 *    90  symbol starts with it
 *    80  every typed word starts a word of the name, the first word first
 *    70  every typed word starts a word of the name
 *    55  the name or symbol contains it
 *    40  every typed word is within a typo or two of a word (or word start)
 *  0–35  overall letter similarity (trigrams), for what is near but not typed
 */
export function similarityScore(query, { symbol = '', name = '' }) {
  const q = searchText(query);
  if (!q) return 0;
  const sym = searchText(symbol);
  const base = sym.split(' ')[0];
  const qCompact = q.replace(/ /g, '');
  if (sym === q || base === qCompact) return 100;
  if (base.startsWith(qCompact) || sym.startsWith(q)) return 90;

  const n = searchText(name);
  const words = n.split(' ').filter(Boolean);
  const typed = q.split(' ').filter(Boolean);
  if (typed.every((t) => words.some((w) => w.startsWith(t)))) {
    return words[0]?.startsWith(typed[0]) ? 80 : 70;
  }
  if (n.includes(q) || n.replace(/ /g, '').includes(qCompact) || sym.includes(qCompact)) return 55;

  /* A typo: each typed word close to a whole word or to the same-length start
     of one. Short words get one slip, longer ones two. */
  const near = (t) => {
    const max = t.length <= 4 ? 1 : 2;
    return [...words, base].some((w) => editDistance(t, w, max) <= max || editDistance(t, w.slice(0, t.length), max) <= max);
  };
  if (typed.every((t) => t.length >= 3 && near(t))) return 40;

  const sim = Math.max(trigramSimilarity(q, n), trigramSimilarity(qCompact, base));
  return sim >= 0.3 ? Math.round(sim * 35) : 0;
}

/** `rows` that match `query`, best first; ties keep their order. */
export function rankBySearch(rows, query, fieldsOf) {
  if (!searchText(query)) return rows;
  return rows
    .map((row, i) => ({ row, i, score: searchScore(query, fieldsOf(row)) }))
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score || a.i - b.i)
    .map((r) => r.row);
}
